import gql from 'graphql-tag';
import { client } from './client';


export const PRO_PLAN_FIELDS = gql`
  fragment proPlanInfo on ProPlanSchema {
    code
    name
    weddings
    discountedPrice
    price
    renew
    listingType
    planType
    includedSeats
  }
`;

export const ALL_PAYMENT_INTENTS = gql`
  fragment paymentIntentFields on StripePaymentIntentSchema {
    id
    status
    nextAction {
      type
      verifyWithMicrodeposits {
        arrivalDate
        hostedVerificationUrl
        microdepositType
      }
    }
    paymentMethod{
      usBankAccount {
        accountHolderType
        accountType
        bankName
        last4
        routingNumber
      }
    }
  }
`;


export const fetchBillingInfo = async (variables) => {
  const result = await client.query({
    query: gql`
      ${PRO_PLAN_FIELDS}
      ${ALL_PAYMENT_INTENTS}
      query{
        billingInfo {
        invoice{
          startingBalance
          subtotal
          total
          amountDue
          customerAddressState
          tax
          taxPercent
          postalCode
          date
          isFirst
          discount
          discountCode
          lines{
            amount
            quantity
            code
            description
            type
            start
            end
          }
          periodStart
          periodEnd
        }
        proPlans {
          month{
            ...proPlanInfo
          }
          monthEnhanced{
            ...proPlanInfo
          }
          monthLegacy{
            ...proPlanInfo
          }
          monthPremium{
            ...proPlanInfo
          }
          yearPremium{
            ...proPlanInfo
          }
        }
        trials{
          pro
          individual
        }
        paymentMethodLast4
        paymentMethodName
        paymentMethodExpMonth
        paymentMethodExpYear
        paymentMethodType
        stripePauseSuscriptionDays
        allPaymentIntents: allPaymentIntents {
          ...paymentIntentFields
        }
        stripeCustomerAddress{
          city
          country
          line1
          line2
          postalCode
          state
        }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return result;
};

export const fetchInvoicePreviews = async () => {
  const results = await client.query({
    query: gql`
      query {
        invoicePreviews {
          code
          startingBalance
          subtotal
          total
          amountDue
          date
          discount
          discountCode
          notDiscountable
          lines {
            amount
            description
            start
            end
            type
            code
          }
          periodStart
          periodEnd
          tax
          taxPercent
        }
      }
    `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const addPromoOrReferral = async (promoReferralCode) => {
  const param = {
    promoReferralCode,
  };
  const result = await client.mutate({
    mutation: gql`
    mutation addPromoOrReferral($promoReferralCode: String!){
        addPromoOrReferral(promoReferralCode:$promoReferralCode){
          login{
            isDisabled
            billing {
              isDelinquent
              hasPaymentMethod
              planningSubscription {
                id
                code
                name
                isTrial
                isPromo
                isPro
                expiresAt
                isExpired
                endedAt
                isEnded
                remainingTrialDays
              }
            }
            promos{
              codeIdentifier
              proSubscriptionCode
              indSubscriptionCode
              stripeCouponIdentifier
            }
          }
        }
      }
    `,
    variables: param,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return result;
};

export const createSetupIntent = async () =>{
  const results = await client.query({
    query: gql`
      query createSetupIntent{
        createSetupIntent{
          clientSecret
        }
      }
      `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results.data;
};

export const attachPaymentMethod = async (key) => {
  const param = {
    input: {
      payment_method: key,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation attachPaymentMethod($input: JSON){
        attachPaymentMethod(input:$input){
          login {
            billing {
              isDelinquent
              hasPaymentMethod
              planningSubscription {
                id
                code
                name
                isTrial
                isPromo
                isPro
                expiresAt
                isExpired
                endedAt
                isEnded
                remainingTrialDays
              }
            }
          }
        }
      }
      `,
    variables: param,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const setPauseSubscription = async (paused) => {
  const param = {
    isPaused: paused,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation setPauseSubscription($isPaused: Boolean!){
        setPauseSubscription(isPaused: $isPaused){
          login {
            id
            isPaused
            pausedAt
          }
        }
      }
      `,
    variables: param,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results.data;
};

export const subscribe = async ({ weddings, renew, convert }) => {
  const param = {
    input: {
      weddings,
      renew,
      convert,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation subscribe($input: SubscribeInput!) {
        subscribe(input: $input) {
          billingAndSubscriptionInfo{
            invoice{
              startingBalance
              subtotal
              total
              amountDue
              customerAddressState
              taxPercent
              tax
              postalCode
              date
              isFirst
              lines{
                amount
                description
                type
                start
                end
                code
              }
              periodStart
              periodEnd
            }
          }
        }
      }
      `,
    variables: param,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const pauseSubscription = async () => {
  const results = await client.mutate({
    mutation: gql`
      mutation pauseSubscription{
        pauseSubscription{
          billingAndSubscriptionInfo{
            subscription{
              id
              isPaused
            }
          }
        }
      }
      `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const renewSubscription = async () => {
  const results = await client.mutate({
    mutation: gql`
       mutation renewSubscription{
        renewSubscription{
          billingAndSubscriptionInfo{
            subscription{
              id
              isPaused
            }
          }
        }
      }
      `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const trial = async () => {
  const results = await client.mutate({
    mutation: gql`
      mutation trial{
        trial{
          login{
            billing {
              planningSubscription {
                name
                isTrial
                isPro
                expiresAt
                isExpired
                remainingTrialDays
              }
            }
          }
        }
      }
      `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const cancelTrial = async () => {
  const results = await client.mutate({
    mutation: gql`
      mutation cancelTrial{
        cancelTrial{
          login{
            billing {
              planningSubscription {
                name
                isTrial
                isPro
                expiresAt
                isExpired
                remainingTrialDays
              }
            }
          }
        }
      }
      `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const unsubscribe = async (cancelReason = 'Did not specify') => {
  const results = await client.mutate({
    mutation: gql`
      mutation unsubscribe($cancellationReason: String){
        unsubscribe(cancellationReason: $cancellationReason){
          billingAndSubscriptionInfo{
            invoice{
              startingBalance
              subtotal
              total
              amountDue
              customerAddressState
              taxPercent
              tax
              postalCode
              date
              isFirst
              lines{
                amount
                description
                type
                start
                end
                code
              }
            }
            paymentMethodType
            paymentMethodLast4
            paymentMethodName
            paymentMethodExpMonth
            paymentMethodExpYear
            stripePauseSuscriptionDays
          }
        }
      }
      `,
    variables: { cancellationReason: cancelReason },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const resumeSubscription = async () => {
  const results = await client.mutate({
    mutation: gql`
      mutation resumeSubscription {
        resumeSubscription {
          billingAndSubscriptionInfo {
            invoice {
              startingBalance
              subtotal
              total
              amountDue
              customerAddressState
              taxPercent
              tax
              postalCode
              date
              isFirst
              lines {
                amount
                description
                type
                start
                end
                code
              }
            }
            paymentMethodType
            paymentMethodLast4
            paymentMethodName
            paymentMethodExpMonth
            paymentMethodExpYear
            stripePauseSuscriptionDays
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};

export const updateCustomerAddress = async ({ city, country, line1, line2, postal_code, state }) => {
  const param = {
    input: {
      city,
      country,
      line1,
      line2,
      postalCode: postal_code,
      state,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation updateCustomerAddress($input: UpdateCustomerAddressInput){
        updateCustomerAddress(input:$input){
          isUpdated
        }
      }
    `,
    variables: param,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return results;
};
